import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { StepIndicator, type StepConfig } from '@/components/ui/StepIndicator';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/hooks/useTheme';
import { useReferralClaim } from '@/hooks/useReferralClaim';
import { cn } from '@/utils/cn';
import { getLoggerWithPrefix } from '@/services/shared/LoggerFactory';
import { useDirectBookingStore } from '@/stores/direct-booking/useDirectBookingStore';
import { DirectPatientInfoStep } from './DirectPatientInfoStep';
import { DirectConfirmationStep } from './DirectConfirmationStep';
import type { SelfReferralFormData } from '../self-referral/types';
import { useShallow } from 'zustand/react/shallow';
import { useDirectBookingActions } from '@/actions/direct-booking/useDirectBookingActions';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const logger = getLoggerWithPrefix('DirectBookingWizard');

const FormIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DIRECT_STEPS: StepConfig[] = [
  {
    key: 'info',
    title: 'Your Details',
    description: 'Tell us about yourself',
    icon: <FormIcon />
  },
  {
    key: 'confirmation',
    title: 'Confirm Booking',
    description: 'Review and finalize',
    icon: <CheckCircleIcon />
  }
];

export const DirectBookingWizard: React.FC = () => {
  const { dietitianId } = useParams<{ dietitianId: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { claimReferral } = useReferralClaim();
  const hasAutoSubmittedRef = useRef(false);
  const {
    step,
    dietitian,
    sessionData,
    patientProfile,
    prefilledConditions,
    isLoading,
    isProcessing,
    error,
    setLoading,
    setError,
    reset,
  } = useDirectBookingStore(useShallow((state) => ({
    step: state.step,
    dietitian: state.dietitian,
    sessionData: state.sessionData,
    patientProfile: state.patientProfile,
    prefilledConditions: state.prefilledConditions,
    isLoading: state.isLoading,
    isProcessing: state.isProcessing,
    error: state.error,
    setLoading: state.setLoading,
    setError: state.setError,
    reset: state.reset,
  })));

  useEffect(() => {
    if (!dietitianId) {
      navigate('/404', { replace: true });
    }
  }, [dietitianId, navigate]);

  const authContext = useAuth();
  logger.debug('Auth context loaded', { hasAuthContext: !!authContext });

  const { isAuthenticated, user, isLoading: isAuthLoading } = authContext || { isAuthenticated: false, user: null, isLoading: false };
  const isPatient = !!user && 'role' in user && user.role === 'patient';

  const { handleInfoSubmit, handleConfirmation, loadUserContext } = useDirectBookingActions({
    dietitianId,
    isAuthenticated,
    claimReferral,
    navigate,
  });

  /**
   * Initial load: if authenticated, fetch patient profile and latest referral (for conditions).
   * If unauthenticated, just stop loading and show the form.
   */
  useEffect(() => {
    let isMounted = true;

    const loadContext = async () => {
      if (!isAuthenticated || !user?.id) {
        if (isMounted) setLoading(false);
        return;
      }

      // Block non-patient authenticated users
      if (!('role' in user) || user.role !== 'patient') {
        navigate('/dashboard', { replace: true });
        return;
      }

      try {
        if (!isMounted) return;
        await loadUserContext(user.id);
      } catch (err) {
        logger.warn('DirectBookingWizard: Failed to load user context', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadContext();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user, loadUserContext, setLoading, navigate]);

  /**
   * Auto-submit patient info when we have an authenticated user and the dietitian profile is loaded.
   * Reuses any stored or previously used health conditions.
   */
  useEffect(() => {
    const canAutoSubmit =
      isAuthenticated &&
      isPatient &&
      !sessionData &&
      step === 'info' &&
      !isProcessing &&
      !hasAutoSubmittedRef.current &&
      patientProfile?.firstName &&
      patientProfile?.lastName &&
      patientProfile?.email;

    if (!canAutoSubmit) return;

    let isCancelled = false;

    const autoSubmit = async () => {
      let conditionsToUse: string[] = [];

      if (prefilledConditions.length > 0) {
        conditionsToUse = prefilledConditions;
      }

      if (isCancelled) return;

      const formData: SelfReferralFormData = {
        first_name: patientProfile!.firstName,
        last_name: patientProfile!.lastName,
        email: patientProfile!.email,
        phone: undefined,
        date_of_birth: patientProfile?.dateOfBirth || '',
        conditions: conditionsToUse,
        additional_notes: ''
      };

      hasAutoSubmittedRef.current = true;

      try {
        await handleInfoSubmit(formData);
      } catch (err) {
        hasAutoSubmittedRef.current = false;
        logger.error('DirectBookingWizard: Auto-submit failed', err);
        setError('Unable to start booking automatically. Please try again.');
      }
    };

    autoSubmit();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, user, patientProfile, handleInfoSubmit, sessionData, step, isProcessing, prefilledConditions, setError]);

  useEffect(() => () => reset(), [reset]);

  if (isLoading || isAuthLoading) {
    logger.debug('Loading state', { isLoading, isAuthLoading });
    return <LoadingSpinner />;
  }

  // Authenticated non-patient users are blocked from this flow
  if (isAuthenticated && !isPatient) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  if (error && !dietitian) {
    logger.error('Error state', { error });
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-red-600">Error</h1>
          <p className="mt-2 text-gray-600">{error}</p>
          <Button className="mt-4" onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </Layout>
    );
  }

  logger.debug('Rendering main content', { step, hasDietitian: !!dietitian });

  return (
    <Layout className={cn('min-h-screen')}>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Progress indicator */}
          <div className="mb-8">
            <StepIndicator
              steps={DIRECT_STEPS}
              currentStep={step}
              onStepClick={() => {}}
              variant="horizontal"
            />
          </div>

          {/* Steps */}
          <div className={cn(
            "rounded-lg p-6",
            isDark ? "bg-secondary-900" : "bg-white shadow-sm"
          )}>
             {step === 'info' && (
                <DirectPatientInfoStep
                    onSubmit={handleInfoSubmit}
                    isLoading={isProcessing}
                    error={error || undefined}
                    initialData={isAuthenticated && user ? {
                        email: user.email
                    } : undefined}
                    title={isAuthenticated ? `Welcome back!` : undefined}
                />
             )}

             {step === 'confirmation' && dietitian && (
                <DirectConfirmationStep
                    dietitian={dietitian}
                    isAuthenticated={isAuthenticated}
                    onConfirm={handleConfirmation}
                    isLoading={isProcessing}
                />
             )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
